<template>
  <div>
    <h2 class="text-h5 mb-4 text-black font-weight-bold">売上情報</h2>

    <!-- 検索フィルタ -->
    <v-card color="grey-lighten-5" class="mt-2" variant="outlined">
      <v-container>
        <v-row>
          <v-spacer />
          <!-- 施設名 -->
          <v-col cols="12" md="3">
            <h5 class="text-subtitle-2 mb-1 text-black">施設名</h5>
            <v-select
              v-model="searchFilter.nurseryId"
              :items="nurseries"
              item-title="nurseryName"
              item-value="nurseryId"
              variant="outlined"
              density="compact"
              hide-details
              bg-color="white"
            ></v-select>
          </v-col>

          <!-- 開始月 -->
          <v-col cols="12" md="3">
            <h5 class="text-subtitle-2 mb-1 text-black">開始月</h5>
            <v-text-field
              v-model="searchFilter.monthFrom"
              type="month"
              variant="outlined"
              density="compact"
              hide-details
              bg-color="white"
              class="date-input-custom"
            ></v-text-field>
          </v-col>

          <!-- 終了月：カレンダーマークが隠れないよう調整 -->
          <v-col cols="12" md="3">
            <h5 class="text-subtitle-2 mb-1 text-black">終了月</h5>
            <v-text-field
              v-model="searchFilter.monthTo"
              type="month"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              bg-color="white"
              class="date-input-custom"
            ></v-text-field>
          </v-col>
          <v-spacer />
        </v-row>

        <v-row>
          <v-spacer />
          <v-col cols="12" md="4">
            <v-btn 
              @click="listSales" 
              :loading="loading" 
              color="primary" 
              variant="elevated" 
              block
            >
              検索
            </v-btn>
          </v-col>
          <v-spacer />
        </v-row>
      </v-container>
    </v-card>

    <!-- テーブル -->
    <div class="pt-4 overflow-auto">
      <v-table class="border text-black">
        <thead class="bg-grey-lighten-3">
          <tr>
            <th class="text-left text-black font-weight-bold">項目名</th>
            <th class="text-left text-black font-weight-bold"></th>
            <th v-for="(item, i) in sales" :key="i" class="text-center text-black font-weight-bold">
              {{ `${item.year}/${item.month}` }}
            </th>
          </tr>
        </thead>
        <tbody v-if="sales && sales.length > 0">
          <template v-for="config in rowConfigs" :key="config.label">
            <tr>
              <td class="text-black font-weight-medium">{{ config.label }}</td>
              <td class="text-black">アップロード数</td>
              <td v-for="(item, i) in sales" :key="i" class="text-center text-black">
                {{ getSaleData(item, config.cat, config.fmt)?.unitOfUpload ?? 0 }}
              </td>
            </tr>
            <tr>
              <td></td>
              <td class="text-black">売上本数</td>
              <td v-for="(item, i) in sales" :key="i" class="text-center text-black">
                {{ getSaleData(item, config.cat, config.fmt)?.unitOfSales ?? 0 }}
              </td>
            </tr>
            <tr class="bg-grey-lighten-4">
              <td></td>
              <td class="text-black">売上金額</td>
              <td v-for="(item, i) in sales" :key="i" class="text-center">
                <strong>
                  {{ (getSaleData(item, config.cat, config.fmt)?.price ?? 0).toLocaleString() }}円
                </strong>
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import LocalDataService from "@/service/LocalDataService";
import { memoruModule } from "@/store/dataModule/memoruModule";
import dayjs from "dayjs";
import { nurseryModule } from "@/store/dataModule/nurseryModule";
import { MemoruPurchaseHistoryListResponse, OperationNurseryResponse } from "chaild-api/lib";

export default defineComponent({
  name: "MemoruSales",

  async mounted() {
    // fetch nurseries
    await this.listNurseries();

    // search sales
    this.searchFilter.monthTo = dayjs().format("YYYY-MM");
    this.searchFilter.monthFrom = dayjs().subtract(2, "month").format("YYYY-MM");
    if (this.nurseries.length > 0) {
      this.searchFilter.nurseryId = this.nurseries[0].nurseryId;
      await this.listSales();
    }
  },

  data() {
    return {
      loading: false,
      searchFilter: {
        nurseryId: "" as string | number,
        monthTo: "",
        monthFrom: "",
      },
      rowConfigs: [
        {
          label: "写真データ",
          cat: "photo",
          fmt: "data",
        },
        {
          label: "写真プリント",
          cat: "photo",
          fmt: "print",
        },
        {
          label: "動画データ",
          cat: "movie",
          fmt: "data",
        },
      ]
    };
  },

  computed: {
    sales: function (): MemoruPurchaseHistoryListResponse | null {
      return memoruModule.sales;
    },
    nurseries: function (): OperationNurseryResponse[] {
      return nurseryModule.nurseries;
    },
  },

  methods: {
    getSaleData(
      monthItem: any,
      category: string,
      format: string
    ) {
      return (
        monthItem?.sales?.find(
          (s: any) =>
            s.mediaCategory === category &&
            s.mediaFormat === format
        ) ?? null
      );
    },
    async listSales() {
      this.loading = true;
      try {
        await memoruModule.listSales({ ...this.searchFilter });
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    
    async listNurseries() {
      this.loading = true;
      const companyId = LocalDataService.getCompanyId();
      if (companyId) {
        await nurseryModule.listNurseries({ companyId }).catch();
      }
      this.loading = false;
    },
  },
});
</script>

<style scoped>
/* Vuetify3のv-tableのヘッダー色を強制 */
:deep(.v-table__wrapper > table > thead > tr > th) {
  color: #000 !important;
  font-size: 0.9rem !important;
}

/* セル内のテキストが見えない場合の保険 */
.text-black {
  color: rgba(0, 0, 0, 0.87) !important;
}
</style>